import {textRequest} from 'api/index'

// ─── HeaderReportResponse ─────────────────────────────────────────────
export interface HeaderReportResponse {
  name: {
    title: string
  }
  sex: {
    title: string
    value: {
      male: string
      female: string
    }
  }
  birth: {
    title: string
  }
  measurement: {
    title: {
      eeg: string
      hrv: string
    }
  }
  eyes_status: {
    title: string
    value: {
      EC: string
      EO: string
      'EC/EO': string
    }
  }
  subject_info_guide: string
  analysis_number: {
    title: string
  }
  disease_code: {
    title: string
  }
  handedness: {
    title: string
    value: {
      both: string
      left: string
      right: string
      unknown: string
    }
  }
}

interface BrainAging {
  threshold: {
    type: string
    low: number
    high: number
  }
  basic: {
    title: string
    section: {
      three_dimensional_graph: {
        number: number
        title: string
        description: {
          main: string
          sub: string[]
        }
      }
      lobes: {
        number: number
        title: string
        score_table: {
          headers: string[]
          rows: Array<{
            lobe: string
            functions: {
              total: string
            }
          }>
          annotation: string
        }
        description: {
          main: string
          sub: string[]
        }
      }
      contour_graph: {
        number: number
        title: string
        score_box: string
        description: {
          class: {
            high: string
            normal: string
            low: string
          }
          main: string
          sub: string
        }
      }
    }
  }
  advanced: {
    title: string
    section: Record<
      string,
      {
        title: string
        description: string[]
        score_by_side: {
          left: string
          right: string
        }
        score_table: {
          headers: string[]
          rows: Array<{
            rois: Array<{
              code: string
              name: string
              translation: string
            }>
            functions: {
              main: string
              sub: string[]
            }
            symptoms: {
              main: string
              sub: string[]
            }
          }>
        }
      }
    >
  }
  status: {
    low: string
    normal: string
    high: string
  }
}

interface InfantYouth {
  title: string
  section: {
    frontal_development: {
      number: number
      title: string
      score_bar: {
        percentile: string
        value: string
        annotation: string
      }
      description: {
        main: string
        sub: string[]
      }
    }
    information_processing_speed: {
      number: number
      title: string
      score_bar: {
        percentile: string
        value: string
        annotation: string
      }
      description: {
        main: string
        sub: string[]
      }
    }
    alpha_development: {
      number: number
      title: string
      description: {
        main: string
        sub: string[]
      }
      analysis_results: {
        title: string
        comments: {
          EC: Record<
            'none' | 'high' | 'normal' | 'low',
            {
              EO: Record<
                'none' | 'high' | 'normal' | 'low',
                {
                  main: string
                  sub: string
                }
              >
            }
          >
          title: string
        }
        annotation: string
      }
    }
    brain_balance: {
      number: number
      title: string
      description: {
        main: string
        sub: string[]
      }
      analysis_results: {
        title: string
        comments: string
        annotation: string
        class: {
          balanced: string
          left: string
          right: string
          weak: string
        }
      }
    }
    brain_age: {
      number: number
      title: string
      description: string[]
      brain_age_image: {
        brain_age: string
        actual_age: string
        age_unit: string
        annotation: string
      }
    }
  }
}

export interface ResEEG {
  header: HeaderReportResponse
  brain_aging: BrainAging
  infant_youth: InfantYouth
  footer: string[]
  title: {
    sub: string
    main: string
  }
}

export interface ResEEGMCI5Stages {
  header: HeaderReportResponse
  threshold: Threshold
  title: string
  section: Section
}

export interface ResEEGMCI8Stages {
  header: HeaderReportResponse
  mci: {
    threshold: Threshold
    title: string
    section: Section8Stages
  }
}

// ─── Threshold ──────────────────────────────────────────
interface Threshold {
  type: string
  stage1: ThresholdStage
  stage2: ThresholdStage
  stage3: ThresholdStage
  stage4: ThresholdStage
  stage5: ThresholdStage
  stage6: ThresholdStage
  stage7: ThresholdStage
  stage8: ThresholdStage
}

interface ThresholdStage {
  low: number
  high: number
}

// ─── Section ────────────────────────────────────────────
interface Section {
  prediction: PredictionSection
  recommendation_table: RecommendationTable
}

interface Section8Stages {
  prediction: PredictionSection8Stages
  recommendation_table: RecommendationTable8Stages
}

// ─── Prediction Section ─────────────────────────────────
interface PredictionSection {
  title: string
  description: string
  opinions: {
    stages: {
      [key: string]: number // e.g., stage1: 1, stage2: 2, ...
    }
    diagnosis: string // contains placeholders like {0}, {1}, etc.
  }
  footer: string[]
}

interface PredictionSection8Stages {
  number: number
  title: string
  opinions: {
    diagnosis: string
    stages: {
      [key: string]: number
    }
    text_box: {
      circle1: string
      circle2: string
      explanation: string
    }
  }
}

// ─── Recommendation Table ───────────────────────────────
interface RecommendationTable {
  title: string
  footer: string[]
  [key: `stage${number}`]: RecommendationStage
}

interface RecommendationTable8Stages {
  number: number
  annotation: string[]
  title: string
  [key: `stage${number}`]: RecommendationStage8Stages
}

interface RecommendationStage {
  stage: {
    description: string
  }
  diagnosis: string
  recommendations: {
    main: string
    sub?: string[]
    summary: {
      fixed_message: string
      box: string
    }
  }
}

interface RecommendationStage8Stages {
  treatment: string
  stage: {
    number: string
    description: string
  }
  recommendations: {
    main: string[]
    sub?: string[]
  }
}

export interface ResHealthReport {
  header: HeaderReportResponse
  five_major_features: FiveMajorFeatures
  brain_connectivity: ResBrainConnectivity
  brain_mapping: ResBrainMapping
  common: {
    score_unit: {
      t_score: string
      percentile: string
    }
    score_annotation: {
      t_score: string
      percentile: string
    }
    footer: string[]
  }
  mci: ResMCI | ResMCIV2
  hrv_analysis: ResHrvAnalysis
}

export interface ResHrvAnalysis {
  title: string
  section: {
    automatic_nervous_balance: {
      number: 1
      title: string
      description: {
        main: string
        sub: string
      }
      pie_chart: {
        title: string
        annotation: {
          parasympathetic: {
            case: string
            symptoms: string
          }
          sympathetic: {
            case: string
            symptoms: string
          }
        }
      }
      analysis_results: {
        title: string
        opinions: {
          stage1: HrvAnalysisStage
          stage2: HrvAnalysisStage
          stage3: HrvAnalysisStage
        }
      }
    }
    two_dimensional_analysis: {
      number: 2
      title: string
      description: string
      analysis_results: {
        title: string
        opinions: {
          stage1: HrvAnalysisStage
          stage2: HrvAnalysisStage
          stage3: HrvAnalysisStage
          stage4: HrvAnalysisStage
          stage5: HrvAnalysisStage
          stage6: HrvAnalysisStage
          stage7: HrvAnalysisStage
          stage8: HrvAnalysisStage
        }
      }
    }
    histogram_analysis: {
      number: 3
      title: 'Análisis del histograma de la variabilidad de la frecuencia cardíaca'
      description: HrvAnalysisStage
      analysis_results: {
        title: 'Resultados del análisis'
        opinions: {
          stage1: HrvAnalysisStage
          stage2: HrvAnalysisStage
          stage3: HrvAnalysisStage
        }
      }
    }
  }
}
interface HrvAnalysisStage {
  main: string
  sub: string
}
export interface ResMCI {
  threshold: {
    type: 'half-open'
    stage1: MCIThresholdStage
    stage2: MCIThresholdStage
    stage3: MCIThresholdStage
    stage4: MCIThresholdStage
    stage5: MCIThresholdStage
  }
  title: string
  section: {
    prediction: {
      number: number
      title: string
      opinions: {
        stages: {
          stage1: number
          stage2: number
          stage3: number
          stage4: number
          stage5: number
        }
        diagnosis: string
        text_box: {
          circle1: string
          circle2: string
          explanation: string
        }
        recommendation_table: {
          stage1: MCIStage
          stage2: MCIStage
          stage3: MCIStage
          stage4: MCIStage
          stage5: MCIStage
        }
        annotation: string[]
      }
    }
  }
}
export interface ResMCIV2 {
  threshold: {
    type: 'half-open'
    stage1: MCIThresholdStage
    stage2: MCIThresholdStage
    stage3: MCIThresholdStage
    stage4: MCIThresholdStage
    stage5: MCIThresholdStage
    stage6: MCIThresholdStage
    stage7: MCIThresholdStage
    stage8: MCIThresholdStage
  }
  title: string
  section: {
    prediction: {
      number: number
      title: string
      opinions: {
        stages: {
          stage1: number
          stage2: number
          stage3: number
          stage4: number
          stage5: number
          stage6: number
          stage7: number
          stage8: number
        }
        diagnosis: string
        text_box: {
          circle1: string
          circle2: string
          explanation: string
        }
      }
    }
    recommendation_table: {
      annotation: string[]
      number: number
      [key: `stage${number}`]: MCIStageV2
      title: string
    }
  }
}
interface MCIThresholdStage {
  low: number
  high: number
}
interface MCIStage {
  stage: {
    number: string
    description: string
  }
  recommendations: string[]
}
export interface MCIStageV2 {
  stage: {
    number: string
    description: string
  }
  recommendations: {
    main: string[]
    sub?: string[]
  }
  treatment: string
}
export interface ResBrainMapping {
  threshold: {
    t_score: BrainMappingScore
    percentile: BrainMappingScore
  }
  basic: {
    title: string
    section: {
      graphs: {
        number: number
        title: string
        annotation: string[]
      }
      lobes: {
        number: number
        title: string
        score_table: {
          headers: string[]
          rows: BrainMappingRow[]
          annotation: string
        }
        description: {
          low: string[]
          high: string[]
        }
      }
    }
  }
  advanced: {
    title: string
    section: {
      frontal: {
        number: number
        title: string
        description: string[]
        score_by_side: {
          left: string
          right: string
        }
        score_table: {
          headers: string[]
          rows: BrainMappingSection[]
        }
      }
      temporal: BrainMappingTemporal
      parietal: BrainMappingTemporal
      occipital: BrainMappingTemporal
    }
  }
  status: {
    low: string
    normal: string
    high: string
  }
}
export interface ResBrainConnectivity {
  threshold: {
    t_score: {
      type: string
      insufficient: number
      low: number
      high: number
      excessive: number
    }
    percentile: {
      type: string
      low: number
      high: number
      excessive: number
      insufficient: number
    }
  }
  difference_threshold: {
    t_score: {
      type: string
      value: number
    }
    percentile: {
      type: string
      value: number
    }
  }
  title: string
  section: {
    intro: {
      description: string[]
      text_box: string[]
      final_words: string
    }
    total_connectivity: {
      number: number
      title: string
      gauge_bar: {
        title: string
        status: StatusChart
      }
      description: string
      analysis_results: {
        title: string
        opinions: StatusOpinions
      }
    }
    inter_connectivity: {
      number: number
      title: string
      description: {
        main: string
        sub: string
      }
      analysis_results: {
        title: string
        opinions: {
          vertical: {
            difference_over: {
              both_high: {
                main: string
                sub: string
              }
              higher_left: {
                main: string
                sub: string
              }
              higher_right: {
                main: string
                sub: string
              }
            }
            difference_under: StatusOpinions
          }
          horizontal: StatusOpinions
        }
      }
      gauge_bars: {
        vertical: {
          left: {
            title: string
            status: StatusChart
          }
          right: {
            title: string
            status: StatusChart
          }
        }
        horizontal: {
          title: string
          status: StatusChart
        }
      }
    }
    intra_connectivity: {
      number: number
      title: string
      description: {
        main: string
        sub: string
      }
      analysis_results: {
        title: string
        opinions: {
          frontal: {
            left: StatusOpinions
            right: StatusOpinions
          }
          pariocci: {
            left: StatusOpinions
            right: StatusOpinions
          }
        }
      }
      gauge_bars: {
        frontal: {
          left: {
            title: string
            status: StatusChart
          }
          right: {
            title: string
            status: StatusChart
          }
        }
        pariocci: {
          left: {
            title: string
            status: StatusChart
          }
          right: {
            title: string
            status: StatusChart
          }
        }
      }
    }
  }
}

export interface FiveMajorFeatures {
  threshold: {
    t_score: {
      type: string
      low: number
      high: number
    }
    percentile: {
      type: string
      low: number
      high: number
    }
  }
  title: string
  section: {
    graph: {
      number: number
      title: string
      description: string[]
      text_box: string[]
    }
    chart: {
      number: number
      title: string
      gauge_bars: {
        sadness: ChartType
        tension: ChartType
        stress: ChartType
        dominant_frequency: ChartType
        inattention: ChartType
      }
      analysis_results: {
        title: string
        opinions: {
          sadness: StatusOpinions
          tension: StatusOpinions
          stress: StatusOpinions
          dominant_frequency: StatusOpinions
          inattention: StatusOpinions
        }
      }
    }
  }
}

interface BrainMappingRow {
  lobe: string
  functions: {
    total: string
    left: string[]
    right: string[]
  }
}
interface BrainMappingScore {
  type: string
  low: number
  high: number
}
interface BrainMappingSection {
  rois: {
    code: string
    name: string
    translation: string
  }[]
  functions: string[]
  symptoms: string[]
}

interface BrainMappingTemporal {
  number: number
  title: string
  description: string[]
  score_by_side: {
    left: string
    right: string
  }
  score_table: {
    headers: string[]
    rows: BrainMappingSection[]
  }
}
interface ChartType {
  title: string
  status: StatusChart
  score_status: StatusChart
}
interface StatusChart {
  low: string
  normal: string
  high: string
}
interface StatusOpinions {
  low: {
    main: string
    sub: string
  }
  normal: {
    main: string
    sub: string
  }
  high: {
    main: string
    sub: string
  }
}

export interface ResHRV {
  header: HeaderReportResponse
  autonomic_nervous_system_stress_analysis: {
    title: string
    section: {
      automatic_nervous_balance: {
        number: number
        title: string
        description: string
        analysis_results: {
          title: string
          opinions: {
            age_group: {
              young: string
              adult: string
              old: string
            }
            sex: {
              male: string
              female: string
            }
            [key: `stage${number}`]: string
          }
        }
        pie_chart: {
          title: string
          annotation: {
            parasympathetic: {
              case: string
              symptoms: string
            }
            sympathetic: {
              case: string
              symptoms: string
            }
          }
        }
      }
      two_dimensional_analysis: {
        number: number
        title: string
        description: string
        analysis_results: {
          title: string
          opinions: {
            stage1: {
              main: string
              sub: string
            }
            stage2: {
              main: string
              sub: string
            }
            stage3: {
              main: string
              sub: string
            }
            stage4: {
              main: string
              sub: string
            }
            stage5: {
              main: string
              sub: string
            }
            stage6: {
              main: string
              sub: string
            }
            stage7: {
              main: string
              sub: string
            }
            stage8: {
              main: string
              sub: string
            }
          }
        }
      }
      histogram_analysis: {
        number: number
        title: string
        description: string
        analysis_results: {
          title: string
          opinions: {
            stage1: {
              main: string
              sub: string
            }
            stage2: {
              main: string
              sub: string
            }
            stage3: {
              main: string
              sub: string
            }
          }
        }
      }
    }
  }
  gas_phase_analysis: {
    title: string
    section: {
      gas_phase: {
        current_state: string
        description: string
      }
      gas_table: {
        headers: string[]
        rows: {
          phase: string
          explanation: string
        }[]
      }
    }
  }
  emotion_analysis: {
    title: string
    section: {
      analysis_results: {
        sadness: {
          title: string
          diagnosis: {
            phases: {
              stage1: string
              stage2: string
              stage3: string
            }
            main: string
            sub: string
          }
        }
        tension: {
          title: string
          diagnosis: {
            phases: {
              stage1: string
              stage2: string
              stage3: string
            }
            main: string
            sub: string
          }
        }
      }
      emotion_table: {
        sadness: {
          title: string
          headers: string[]
          rows: {
            phase: string
            explanation: string
          }[]
        }
        tension: {
          title: string
          headers: string[]
          rows: {
            phase: string
            explanation: string
          }[]
        }
      }
    }
  }
  footer: string[]
}

export const getMultiLanguageEEGReportApi = (lang: string) => {
  return textRequest<ResEEG>({
    method: 'get',
    path: `/engine/analysis/eeg/summary-report/static-content/texts`,
    params: {
      lang,
    },
  })
}

export const getMultiLanguageHRVReportApi = (lang: string) => {
  return textRequest<ResHRV>({
    method: 'get',
    path: `/engine/analysis/hrv/summary-report/static-content/texts`,
    params: {
      lang,
    },
  })
}

export const getMultiLanguageEEGMCIReportApi = (
  lang: string,
  version: number,
) => {
  return textRequest<ResMCI>({
    method: 'get',
    path: `/engine/analysis/eeg/mci-screening/static-content/texts`,
    params: {
      lang,
      version,
    },
  })
}

export const getMultiLanguageHealthCenterReportApi = (
  lang: string,
  version: number,
) => {
  return textRequest<ResHealthReport>({
    method: 'get',
    path: `/engine/analysis/health-checkup/static-content/texts`,
    params: {
      lang,
      version,
    },
  })
}
